import {
  SortingState,
  TableOptions,
  flexRender,
  getCoreRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { OrderTypeProps, TableProps } from '../interface/interface';
import { useToDefineCols } from '../hooks/useToDefineCols';
import { useState } from 'react';
import { ChevronDownIcon, ChevronUpIcon } from '@heroicons/react/24/outline';
import { PaginationTriggers } from './PaginationTriggers';
import { Search } from './Search';
import OrderBulkUpdateButton from './OrderBulkUpdateButton';
import Skeleton from 'react-loading-skeleton';
import { CreateShipmentButton } from './CreateShipmentButton';
import { DownloadManifestSummary } from './DownloadManifestSummary';
import FirstOrderCheckBox from './FirstOrderCheckBox';
import GuestOrderCheckBox from './GuestUserCheckbox';

export function Table(props: TableProps) {
  const [columns] = useToDefineCols(props.columns);
  const [sorting, setSorting] = useState<SortingState>([]);

  const tableOptions: TableOptions<any> = {
    columns,
    data: props.data,
    state: {},
    getCoreRowModel: getCoreRowModel(),
  };

  if (props.enableRowSelection) {
    tableOptions.state = {
      ...tableOptions.state,
      rowSelection: props.rowSelection?.state,
    };
    tableOptions.onRowSelectionChange = props.rowSelection?.setter;
    tableOptions.getRowId = (row) => row.select;
  }

  /**
   * If sorting enabled.
   */
  if (props.enableSorting) {
    tableOptions.enableMultiSort = !!props.enableMultiSort;
    tableOptions.state = { ...tableOptions.state, sorting };
    tableOptions.onSortingChange = setSorting;
    tableOptions.getSortedRowModel = getSortedRowModel();
  }

  /**
   * If we need manual pagination
   */
  if (props.enablePagination) {
    tableOptions.state = {
      ...tableOptions.state,
      pagination: props.pagintation?.state,
    };
    tableOptions.getPaginationRowModel = getPaginationRowModel();
    tableOptions.manualPagination = true;
    tableOptions.onPaginationChange = props.pagintation?.setter;
  }

  const table = useReactTable(tableOptions);

  function getTitle(order: OrderTypeProps) {
    if (order === OrderTypeProps.ORDER) return 'Orders';
    if (order === OrderTypeProps.PRODUCT) return 'Products';
    return 'Patients';
  }
  return (
    <>
      {props.enableSearch && props.search && (
        <div
          className={`pb-6 flex items-center ${props?.order ? 'justify-between' : 'justify-end'}`}
        >
          {props?.order && (
            <div className="flex items-center">
              <p className="text-base text-primary-blue-500">
                Total {getTitle(props.order)}:{' '}
                {props.childLoader ? (
                  <span className="loader"></span>
                ) : (
                  <span className="text-xl font-semibold">
                    {props?.data?.length}
                  </span>
                )}
              </p>
              {props?.order === 'order' && (
                <>
                  <OrderBulkUpdateButton
                    orderBulkUpdate={props.orderBulkUpdate}
                    isPlo={props.isPlo}
                  />
                  <CreateShipmentButton />
                  <DownloadManifestSummary />
                  <FirstOrderCheckBox
                    firstOrderFilter={!!props?.firstOrderFilter}
                    setFirstOrderFilter={props?.setFirstOrderFilter}
                    setGuestOrderFilter={props?.setGuestOrderFilter}
                  />
                  <GuestOrderCheckBox
                    guestOrderFilter={!!props?.guestOrderFilter}
                    setGuestOrderFilter={props?.setGuestOrderFilter}
                    setFirstOrderFilter={props?.setFirstOrderFilter}
                  />
                </>
              )}
            </div>
          )}
          <Search
            value={props.search.value}
            changeHandler={props.search.changeHandler}
            submitSearch={props.search.submitSearch}
          />
        </div>
      )}
      <table className="min-w-full divide-y divide-gray-300">
        <thead className="bg-secondary-cornflowerblue-50 rounded-lg sticky-header">
          {table.getHeaderGroups().map((headerGroup) => (
            <tr key={headerGroup.id}>
              {headerGroup.headers.map((header) => (
                <th
                  key={header.id}
                  scope="col"
                  className="py-4 text-left text-xs 2xl:text-base font-semibold text-primary-blue-500 sm:pl-6"
                  style={
                    props.columns.find(
                      (f) => f.key === header.column.columnDef.id,
                    )?.width
                      ? {
                          width: `${
                            props.columns.find(
                              (f) => f.key === header.column.columnDef.id,
                            )?.width
                          }`,
                        }
                      : { width: 'auto' }
                  }
                >
                  {header.isPlaceholder ? null : props.columns.find(
                      (f) => f.key === header.column.columnDef.id,
                    )?.isColumnContainsOnlyButtons ? (
                    <span className="sr-only">
                      {flexRender(
                        header.column.columnDef.header,
                        header.getContext(),
                      )}
                    </span>
                  ) : !header.column.columnDef.enableSorting ? (
                    flexRender(
                      header.column.columnDef.header,
                      header.getContext(),
                    )
                  ) : (
                    <div
                      {...(props.enableMultiSort
                        ? {
                            className: header.column.getCanSort()
                              ? 'cursor-pointer select-none flex min-w-[36px]'
                              : '',
                            onClick: () =>
                              header.column.toggleSorting(undefined, true),
                          }
                        : {
                            className: header.column.getCanSort()
                              ? 'cursor-pointer select-none flex min-w-[36px]'
                              : '',
                            onClick: header.column.getToggleSortingHandler(),
                          })}
                    >
                      {flexRender(
                        header.column.columnDef.header,
                        header.getContext(),
                      )}
                      {{
                        asc: <ChevronUpIcon className="h-3 w-3 m-1" />,
                        desc: <ChevronDownIcon className="h-3 w-3 m-1" />,
                      }[header.column.getIsSorted() as string] ?? null}
                    </div>
                  )}
                </th>
              ))}
            </tr>
          ))}
        </thead>
        <tbody className="divide-y divide-gray-200">
          {props?.order &&
          [
            OrderTypeProps.ORDER,
            OrderTypeProps.PRODUCT,
            OrderTypeProps.DOCTOR,
          ].includes(props.order) &&
          props?.childLoader
            ? Array.from({ length: 5 }).map((_, index) => (
                <tr key={index}>
                  {/* Render the same number of cells as in the actual data */}
                  {props.columns.map((col) => (
                    <td
                      key={col.key}
                      className={`py-4 2xl:py-5 pl-1 pr-1 relative bg-white ${
                        col.isColumnContainsOnlyButtons
                          ? 'relative whitespace-nowrap text-xs 2xl:text-sm font-medium sm:pr-6'
                          : 'whitespace-nowrap text-xs 2xl:text-sm font-regular text-primary-blue-500 sm:pl-6'
                      }`}
                    >
                      <Skeleton />
                    </td>
                  ))}
                </tr>
              ))
            : table.getRowModel().rows.map((row) => (
                <tr key={row.id} className={``}>
                  {row.getVisibleCells().map((cell) => (
                    <td
                      key={cell.id}
                      className={`py-4 2xl:py-5 pl-1 pr-1 relative ${
                        props.columns.find(
                          (col: { key: string | undefined }) =>
                            cell.column.columnDef.id === col.key,
                        )?.isColumnContainsOnlyButtons
                          ? `relative whitespace-nowrap text-xs 2xl:text-sm font-medium sm:pr-6 ${
                              row.original.product_discontinued
                                ? 'bg-secondary-pink-100'
                                : row?.original?.inventory_quantity === 0
                                  ? 'bg-secondary-pink-100'
                                  : 'bg-white'
                            }`
                          : `whitespace-nowrap text-xs 2xl:text-sm font-regular text-primary-blue-500 sm:pl-6 ${
                              row.original.product_discontinued
                                ? 'bg-secondary-pink-100 opacity-50'
                                : row?.original?.inventory_quantity === 0
                                  ? 'bg-secondary-pink-100'
                                  : 'bg-white'
                            }`
                      }`}
                    >
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext(),
                      )}
                    </td>
                  ))}
                </tr>
              ))}
        </tbody>
      </table>
      {props.enablePagination && props.pagintation ? (
        <PaginationTriggers
          totalRecords={props.pagintation.totalRecords}
          currentPage={props.pagintation.state}
          pageHandler={(page: number) => {
            if (
              props.pagintation &&
              page * props.pagintation.state.pageSize <=
                props.pagintation.totalRecords
            ) {
              props.pagintation?.setter((prev) => ({
                ...prev,
                pageIndex: page,
              }));
            }
          }}
        />
      ) : null}
    </>
  );
}
